import { useState } from "react";
import { View } from "react-native";
import { SvgXml } from "react-native-svg";
import {
  disableTotp,
  startTotpSetup,
  useTotpStatus,
  verifyTotpCode,
  type TotpSetup,
} from "~/lib/totp";
import { useTheme } from "~/styles/theme";
import { Button, Card, Input, Text } from "./ui";

/** Account settings section: turn optional two-factor authentication on or off. */
export function TwoFactorCard() {
  const { colors } = useTheme();
  const status = useTotpStatus();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  };

  const begin = () => run(async () => setSetup(await startTotpSetup()));
  const confirm = () =>
    run(async () => {
      setRecoveryCodes(await verifyTotpCode(code));
      setSetup(null);
      setCode("");
      await status.refresh();
    });
  const turnOff = () =>
    run(async () => {
      await disableTotp();
      setRecoveryCodes([]);
      await status.refresh();
    });
  const cancel = () => {
    setSetup(null);
    setCode("");
    setError("");
  };

  return (
    <Card style={{ gap: 12 }}>
      <Text variant="title">Two-factor authentication</Text>
      {status.loading ? (
        <Text variant="caption" color={colors.muted}>
          Checking…
        </Text>
      ) : setup ? (
        <>
          <Text variant="caption" color={colors.muted}>
            Scan this code with an authenticator app, or type the key in by
            hand. Then enter the 6-digit code it shows.
          </Text>
          <View style={{ alignSelf: "center", width: 200, height: 200 }}>
            <SvgXml xml={setup.qrSvg} width="100%" height="100%" />
          </View>
          <Input
            label="Setup key"
            value={setup.secret}
            onChangeText={() => undefined}
            disabled
          />
          <Input
            label="6-digit code"
            value={code}
            onChangeText={setCode}
            keyboardType="number-pad"
          />
          {error ? (
            <Text variant="caption" color={colors.danger}>
              {error}
            </Text>
          ) : null}
          <Button
            label="Turn on two-factor"
            disabled={code.trim().length !== 6}
            loading={busy}
            onPress={() => void confirm()}
            fullWidth
          />
          <Button label="Cancel" variant="ghost" onPress={cancel} fullWidth />
        </>
      ) : status.enabled ? (
        <>
          <Text variant="caption" color={colors.muted}>
            On. Signing in asks for a code from your authenticator app.
          </Text>
          {recoveryCodes.length ? (
            <>
              <Text variant="caption" color={colors.muted}>
                Save these recovery codes somewhere safe. Each works once if you
                lose your authenticator, and they will not be shown again.
              </Text>
              <Text variant="body">{recoveryCodes.join("  ")}</Text>
              <Button
                label="I have saved them"
                variant="ghost"
                onPress={() => setRecoveryCodes([])}
                fullWidth
              />
            </>
          ) : null}
          {error ? (
            <Text variant="caption" color={colors.danger}>
              {error}
            </Text>
          ) : null}
          <Button
            label="Turn off two-factor"
            variant="danger"
            loading={busy}
            onPress={() => void turnOff()}
            fullWidth
          />
        </>
      ) : (
        <>
          <Text variant="caption" color={colors.muted}>
            Optional. Add a code from an authenticator app to sign-in for extra
            protection.
          </Text>
          {error ? (
            <Text variant="caption" color={colors.danger}>
              {error}
            </Text>
          ) : null}
          <Button
            label="Set up two-factor"
            loading={busy}
            onPress={() => void begin()}
            fullWidth
          />
        </>
      )}
    </Card>
  );
}
